/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';

import { isEmpty } from '@ember/utils';
import QRCode from 'qrcode-generator';
import { classNames, tagName } from '@ember-decorators/component';

@tagName('div')
@classNames('qr-code-container')
export default class SidekickCodes extends Component {
  @tracked goal = null;
  @tracked group = null;
  @tracked page = null;
  @tracked person = null;
  @tracked topic = null;
  avatar = false;
  codeOnly = false;
  @tracked size = '80';
  @tracked type = 'person';
  label = null;

  get font() {
    switch (this.size) {
      case '80':
        return 'bold 12px Arial';
      case '150':
        return 'bold 18px Arial';
      case '200':
        return 'bold 18px Arial';
      default:
        return 'bold 12px Arial';
    }
  }

  get iconSize() {
    switch (this.size) {
      case '80':
        return 24;
      case '150':
        return 32;
      case '200':
        return 32;
      default:
        return 24;
    }
  }

  get labelHeight() {
    switch (this.size) {
      case '80':
        return 40;
      case '150':
        return 60;
      case '200':
        return 60;
      default:
        return 40;
    }
  }

  // labelColour and qrColour are generally the same
  get labelColour() {
    switch (this.type) {
      case 'group':
        return '#FFE1EA';
      case 'person':
        return '#FFE1EA';
      case 'topic':
        return '#DFEDFB';
      case 'objective':
        return '#DFEDFB';
      default:
        return '#FFE1EA';
    }
  }

  get borderColour() {
    switch (this.type) {
      case 'group':
        return '#FFE1EA';
      case 'person':
        return '#FFE1EA';
      case 'topic':
        return '#DFEDFB';
      case 'objective':
        return '#DFEDFB';
      default:
        return '#FFE1EA';
    }
  }

  // labelTextColour and qrColour are generally the same
  get labelTextColour() {
    switch (this.type) {
      case 'group':
        return '#FC86AA';
      case 'person':
        return '#FC86AA';
      case 'topic':
        return '#75B9F0';
      case 'objective':
        return '#75B9F0';
      default:
        return '#FC86AA';
    }
  }

  get qrColour() {
    switch (this.type) {
      case 'group':
        return '#FC86AA';
      case 'person':
        return '#FC86AA';
      case 'topic':
        return '#75B9F0';
      case 'objective':
        return '#75B9F0';
      default:
        return '#FC86AA';
    }
  }

  backgroundColour = '#FFFFFF';
  borderRadius = 12;

  get qrData() {
    let data = ['goal', 'group', 'page', 'person', 'topic'].reduce(
      (data, prop) => {
        let entity = this.get(prop);
        if (!isEmpty(entity)) {
          data[`${prop}Id`] = entity.get('id');
        }
        return data;
      },
      {},
    );
    return JSON.stringify(data);
  }

  didInsertElement() {
    super.didInsertElement(...arguments);
    this.generateStyledQRCode();
  }

  didUpdateAttrs() {
    super.didUpdateAttrs(...arguments);
    this.generateStyledQRCode();
  }

  generateStyledQRCode() {
    const qr = QRCode(0, 'H');
    qr.addData(this.qrData);
    qr.make();

    const canvas = this.element.querySelector('canvas');
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    const moduleCount = qr.getModuleCount();

    const padding = 20;
    const borderWidth = 8;
    const borderRadius = this.borderRadius;
    const labelHeight = this.labelHeight; // Adjusted for better proportions
    const qrAreaSize = parseInt(this.size);
    const canvasWidth = qrAreaSize + padding * 2;
    const canvasHeight = qrAreaSize + padding * 2 + labelHeight;

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw outer rounded background with border
    ctx.fillStyle = this.borderColour;
    ctx.beginPath();
    ctx.moveTo(borderRadius, 0);
    ctx.lineTo(canvasWidth - borderRadius, 0);
    ctx.arcTo(canvasWidth, 0, canvasWidth, borderRadius, borderRadius);
    ctx.lineTo(canvasWidth, canvasHeight - borderRadius);
    ctx.arcTo(
      canvasWidth,
      canvasHeight,
      canvasWidth - borderRadius,
      canvasHeight,
      borderRadius,
    );
    ctx.lineTo(borderRadius, canvasHeight);
    ctx.arcTo(0, canvasHeight, 0, canvasHeight - borderRadius, borderRadius);
    ctx.lineTo(0, borderRadius);
    ctx.arcTo(0, 0, borderRadius, 0, borderRadius);
    ctx.closePath();
    ctx.fill();

    // Draw inner white background
    ctx.fillStyle = this.backgroundColour;
    ctx.beginPath();
    ctx.moveTo(borderWidth + borderRadius, borderWidth);
    ctx.lineTo(canvasWidth - borderWidth - borderRadius, borderWidth);
    ctx.arcTo(
      canvasWidth - borderWidth,
      borderWidth,
      canvasWidth - borderWidth,
      borderWidth + borderRadius,
      borderRadius,
    );
    ctx.lineTo(
      canvasWidth - borderWidth,
      qrAreaSize + borderWidth - borderRadius + padding,
    );
    ctx.arcTo(
      canvasWidth - borderWidth,
      qrAreaSize + borderWidth + padding,
      canvasWidth - borderWidth - borderRadius,
      qrAreaSize + borderWidth + padding,
      borderRadius,
    );
    ctx.lineTo(borderWidth + borderRadius, qrAreaSize + borderWidth + padding);
    ctx.arcTo(
      borderWidth,
      qrAreaSize + borderWidth + padding,
      borderWidth,
      qrAreaSize + borderWidth - borderRadius + padding,
      borderRadius,
    );
    ctx.lineTo(borderWidth, borderWidth + borderRadius);
    ctx.arcTo(
      borderWidth,
      borderWidth,
      borderWidth + borderRadius,
      borderWidth,
      borderRadius,
    );
    ctx.closePath();
    ctx.fill();

    // Draw QR code
    const tileSize = qrAreaSize / moduleCount;
    const qrX = (canvasWidth - moduleCount * tileSize) / 2;
    const qrY = (canvasHeight - labelHeight - moduleCount * tileSize) / 2;

    for (let row = 0; row < moduleCount; row++) {
      for (let col = 0; col < moduleCount; col++) {
        ctx.fillStyle = qr.isDark(row, col)
          ? this.qrColour
          : this.backgroundColour;
        ctx.fillRect(
          qrX + col * tileSize,
          qrY + row * tileSize,
          tileSize,
          tileSize,
        );
      }
    }

    // Draw label background
    ctx.fillStyle = this.labelColour;
    ctx.beginPath();
    ctx.moveTo(borderRadius, canvasHeight - labelHeight);
    ctx.lineTo(canvasWidth - borderRadius, canvasHeight - labelHeight);
    ctx.arcTo(
      canvasWidth,
      canvasHeight - labelHeight,
      canvasWidth,
      canvasHeight - labelHeight + borderRadius,
      borderRadius,
    );
    ctx.lineTo(canvasWidth, canvasHeight - borderRadius);
    ctx.arcTo(
      canvasWidth,
      canvasHeight,
      canvasWidth - borderRadius,
      canvasHeight,
      borderRadius,
    );
    ctx.lineTo(borderRadius, canvasHeight);
    ctx.arcTo(0, canvasHeight, 0, canvasHeight - borderRadius, borderRadius);
    ctx.lineTo(0, canvasHeight - labelHeight + borderRadius);
    ctx.arcTo(
      0,
      canvasHeight - labelHeight,
      borderRadius,
      canvasHeight - labelHeight,
      borderRadius,
    );
    ctx.closePath();
    ctx.fill();

    // Create and load icon
    const icon = new Image();

    // Create a simple QR code scanner icon as SVG
    const iconSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="${this.labelTextColour}">
        <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5zm13-2h3v3h-3v-3zm-5 0h3v3h-3v-3zm5 5h3v3h-3v-3zm-5 0h3v3h-3v-3z"/>
      </svg>`;

    icon.src = `data:image/svg+xml;base64,${btoa(iconSvg)}`;

    icon.onload = () => {
      const iconSize = this.iconSize;
      const labelYOffset = 6; // Increase this number to move label up
      const labelY = canvasHeight - labelHeight / 2 - labelYOffset;
      const text = this.label || 'Sidekick Code';
      ctx.font = this.font;
      const textWidth = ctx.measureText(text).width;

      // Calculate total width of icon + spacing + text
      const totalWidth = iconSize + 8 + textWidth; // 8px spacing between icon and text

      // Calculate starting x position to center the whole group
      const startX = (canvasWidth - totalWidth) / 2;

      // Draw icon on the left
      ctx.drawImage(icon, startX, labelY - iconSize / 2, iconSize, iconSize);

      // Draw text to the right of the icon
      ctx.fillStyle = this.labelTextColour;
      ctx.textAlign = 'left';
      ctx.fillText(
        text,
        startX + iconSize + 8,
        labelY + 6, // This +6 adjusts text position vertically
      );
    };
  }
}
